import { MessageCircle } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { type AvatarStyle, getAvatarStyle } from "./dashboard-primitives";
import { RoomCard, type RoomCardProps } from "./dashboard-widgets";

export type { AvatarStyle } from "./dashboard-primitives";
export { getAvatarStyle } from "./dashboard-primitives";

// ============================================================================
// Phase Progress Component
// ============================================================================

interface Phase {
	name: string;
	progress: number;
	status: string;
}

interface PhaseProgressProps {
	phases: readonly Phase[];
	title: ReactNode;
	daysText?: string;
}

export function PhaseProgress({
	phases,
	title,
	daysText: _daysText,
}: PhaseProgressProps) {
	const { t } = useTranslation();
	return (
		<div className="rounded-2xl border border-dashboard-border bg-dashboard-paper dashboard-surface-card-light p-7">
			<p className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-dashboard-muted">
				PHASE PROGRESS
			</p>
			<h3 className="mt-2 font-display text-2xl font-medium text-dashboard-text">
				{title}
			</h3>
			<div className="mt-5 max-h-[282px] overflow-y-auto pr-1">
				{phases.length === 0 && (
					<p className="font-body text-sm italic text-dashboard-muted">
						{t("homeProgress.noPhases")}
					</p>
				)}
				{phases.map((phase, i) => (
					<div
						key={phase.name}
						className="py-2.5"
						style={{
							borderBottom:
								i < phases.length - 1
									? "1px solid var(--color-dashboard-border-light)"
									: "",
						}}
					>
						<div className="flex items-baseline justify-between pt-[10px] pb-[10px] gap-3">
							<span className="font-display text-base italic font-medium text-dashboard-text">
								{phase.name}
							</span>
							<span
								className="font-mono text-[10.5px] font-semibold tabular-nums"
								style={{
									color:
										phase.progress > 0
											? "var(--color-dashboard-accent-dark)"
											: "var(--color-dashboard-progress-empty)",
								}}
							>
								{phase.progress}%
							</span>
						</div>
						<div className="mt-.5 h-0.75 rounded-full bg-dashboard-border-soft overflow-hidden">
							<div
								className="h-full rounded-full"
								style={{
									width: `${phase.progress}%`,
									background:
										phase.progress === 100
											? "var(--color-dashboard-accent-dark)"
											: phase.progress > 0
												? "var(--color-dashboard-accent)"
												: "var(--color-dashboard-progress-empty)",
								}}
							/>
						</div>
						<p className="mt-2 mb-2 font-body text-xs italic text-dashboard-muted">
							{phase.status}
						</p>
					</div>
				))}
			</div>
		</div>
	);
}

// ============================================================================
// Room Cards Grid
// ============================================================================

interface RoomCardsGridProps {
	rooms: readonly RoomCardProps[];
	roomCount: number;
	onViewAll?: () => void;
}

export function RoomCardsGrid({
	rooms,
	roomCount,
	onViewAll,
}: RoomCardsGridProps) {
	return (
		<div>
			<div className="mb-4 flex items-baseline justify-between">
				<span className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-dashboard-muted">
					ROOMS · {roomCount}
				</span>
				<button
					type="button"
					className="bg-none border-none font-display text-sm italic text-dashboard-accent-dark cursor-pointer"
					onClick={onViewAll}
				>
					Open all →
				</button>
			</div>
			<div className="grid grid-cols-3 gap-3">
				{rooms.map((room) => (
					<RoomCard key={room.name} {...room} />
				))}
			</div>
		</div>
	);
}

// ============================================================================
// Upcoming Events Component
// ============================================================================

interface EventItem {
	day: string;
	month: string;
	title: string;
	subtitle: string;
	tag: string;
	tagType: "warm" | "urgent" | "neutral";
}

interface UpcomingEventsProps {
	events: readonly EventItem[];
	title: ReactNode;
}

export function UpcomingEvents({ events, title }: UpcomingEventsProps) {
	return (
		<div>
			<div className="mb-5">
				<p className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-dashboard-muted">
					UPCOMING · NEXT 14 DAYS
				</p>
				<h3 className="mt-2 font-display text-[32px] font-medium text-dashboard-text tracking-tight">
					{title}
				</h3>
			</div>
			<div className="rounded-2xl border border-dashboard-border dashboard-surface-card-light">
				{events.map((item, i) => (
					<div
						key={item.title}
						className="grid grid-cols-[64px_1fr_100px] items-center gap-4 px-6 py-4"
						style={{
							borderBottom:
								i < events.length - 1
									? "1px solid var(--color-dashboard-border-light)"
									: "",
						}}
					>
						<div className="text-center">
							<p className="font-display text-[28px] font-medium text-dashboard-text tabular-nums leading-none">
								{item.day}
							</p>
							<p className="font-mono text-[9px] font-semibold uppercase tracking-[0.22em] text-dashboard-muted mt-1">
								{item.month}
							</p>
						</div>
						<div>
							<p className="font-display italic text-[17px] font-medium text-dashboard-text">
								{item.title}
							</p>
							<p className="font-body text-xs text-dashboard-muted mt-1">
								{item.subtitle}
							</p>
						</div>
						<div className="text-right">
							<span
								className="inline-block rounded-full px-3 py-1 font-mono text-[9px] font-semibold uppercase tracking-[0.18em]"
								style={{
									background:
										item.tagType === "warm"
											? "linear-gradient(var(--color-dashboard-warm-100), var(--color-dashboard-warm-250))"
											: item.tagType === "urgent"
												? "color-mix(in srgb, var(--color-dashboard-accent) 12%, transparent)"
												: "color-mix(in srgb, var(--color-dashboard-muted) 10%, transparent)",
									color:
										item.tagType === "warm" || item.tagType === "urgent"
											? "var(--color-dashboard-accent-dark)"
											: "var(--color-dashboard-muted-alt)",
								}}
							>
								{item.tag}
							</span>
						</div>
					</div>
				))}
			</div>
		</div>
	);
}

// ============================================================================
// Team Section Component
// ============================================================================

interface TeamMember {
	initials: string;
	role: string;
	name: string;
	style: string;
	online: boolean;
	avatarStyle: AvatarStyle;
	userId?: string;
}

interface TeamSectionProps {
	members: readonly TeamMember[];
	title: ReactNode;
	memberCount: number;
	onMessageUser?: (userId: string) => void;
}

export function TeamSection({
	members,
	title,
	memberCount,
	onMessageUser,
}: TeamSectionProps) {
	return (
		<div>
			<div className="mb-5">
				<p className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-dashboard-muted">
					YOUR TEAM · {memberCount}
				</p>
				<h3 className="mt-2 font-display text-[32px] font-medium text-dashboard-text tracking-tight">
					{title}
				</h3>
			</div>
			<div className="rounded-2xl border border-dashboard-border  p-5 dashboard-surface-card-light">
				{members.length === 0 ? (
					<p className="py-4 font-body text-sm italic text-dashboard-muted">
						No team members yet.
					</p>
				) : (
					members.map((member, i) => (
						<div
							key={member.name}
							className="flex items-center gap-4 py-3"
							style={{
								borderBottom:
									i < members.length - 1
										? "1px solid var(--color-dashboard-border-light)"
										: "",
							}}
						>
							<div className="relative shrink-0">
								<div
									className="flex h-11 w-11 items-center justify-center rounded-full font-body text-[13px] font-bold dashboard-avatar-light"
									style={getAvatarStyle(member.avatarStyle)}
								>
									{member.initials}
								</div>
								{member.online && (
									<span
										className="absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-dashboard-paper"
										style={{
											background: "var(--color-dashboard-accent-hover)",
										}}
									/>
								)}
							</div>
							<div className="flex-1">
								<p className="font-mono text-[8.5px] font-semibold uppercase tracking-[0.16em] text-dashboard-muted">
									{member.role}
								</p>
								<p className="font-display italic text-[16px] font-medium text-dashboard-text mt-0.5">
									{member.name}
								</p>
								<p className="font-body text-[11.5px] text-dashboard-muted mt-0.5">
									{member.style}
								</p>
							</div>
							{member.userId && onMessageUser && (
								<button
									type="button"
									className="flex h-9 w-9 items-center justify-center rounded-lg border-none cursor-pointer text-dashboard-accent-dark bg-dashboard-warm-100 "
									aria-label={`Message ${member.name}`}
									onClick={() => {
										const uid = member.userId;
										if (uid) onMessageUser(uid);
									}}
								>
									<MessageCircle className="h-4 w-4" />
								</button>
							)}
						</div>
					))
				)}
			</div>
		</div>
	);
}

// ============================================================================
// Recent Activity Component
// ============================================================================

interface ActivityItem {
	initials: string;
	avatarStyle: AvatarStyle;
	title: string;
	subtitle: string;
	time: string;
}

interface RecentActivityProps {
	activities: readonly ActivityItem[];
	title: ReactNode;
	entryCount: number;
}

export function RecentActivity({
	activities,
	title,
	entryCount,
}: RecentActivityProps) {
	return (
		<div>
			<div className="mb-5">
				<p className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-dashboard-muted">
					RECENT ACTIVITY · {entryCount} ENTRIES THIS WEEK
				</p>
				<h3 className="mt-2 font-display text-[32px] font-medium text-dashboard-text tracking-tight">
					{title}
				</h3>
			</div>
			<div className="rounded-2xl border border-dashboard-border bg-dashboard-paper dashboard-surface-card-light max-h-[380px] overflow-y-auto scrollbar-hide">
				{activities.length === 0 ? (
					<p className="py-4 px-5 font-body text-sm italic text-dashboard-muted">
						No recent activity.
					</p>
				) : (
					activities.map((item, i) => (
						<div
							// biome-ignore lint/suspicious/noArrayIndexKey: activity items have no stable id; title may repeat
							key={`${item.title}-${i}`}
							className="grid grid-cols-[40px_1fr_80px] items-center gap-3 px-5 py-4"
							style={{
								borderBottom:
									i < activities.length - 1
										? "1px solid var(--color-dashboard-border-light)"
										: "",
							}}
						>
							<div
								className="flex h-7.5 w-7.5 items-center justify-center rounded-full font-body text-[9.5px] font-bold"
								style={getAvatarStyle(item.avatarStyle)}
							>
								{item.initials}
							</div>
							<div>
								<p className="font-display italic text-[14px] font-medium text-dashboard-text">
									{item.title}
								</p>
								<p className="font-body text-[11.5px] text-dashboard-muted mt-0.5">
									{item.subtitle}
								</p>
							</div>
							<div className="font-mono text-[9px] text-dashboard-muted uppercase tracking-[0.14em] text-right">
								{item.time}
							</div>
						</div>
					))
				)}
			</div>
		</div>
	);
}

// ============================================================================
// AI Recommendations Component
// ============================================================================
